/**
 * Comprehensive Documentation Component
 * Provides detailed guides for CI/CD integration, token setup, scanning processes, and security benefits
 */

import React, { useState } from 'react';
import { BookOpen, Copy, ExternalLink, Shield, GitBranch, AlertTriangle, CheckCircle, Code, Database, Globe, Bot, Search, FileText, Settings, Info } from 'lucide-react';

const Documentation = () => {
  const [activeSection, setActiveSection] = useState('overview');
  const [copiedCode, setCopiedCode] = useState('');

  const copyToClipboard = (text, identifier) => {
    navigator.clipboard.writeText(text);
    setCopiedCode(identifier);
    setTimeout(() => setCopiedCode(''), 2000);
  };

  const sections = [
    { id: 'overview', title: '📖 Overview', icon: BookOpen },
    { id: 'github-setup', title: '🔧 GitHub Setup', icon: Settings },
    { id: 'ci-cd', title: '🔄 CI/CD Integration', icon: GitBranch },
    { id: 'scan-types', title: '🔍 Scan Types', icon: Search },
    { id: 'mitigation', title: '🛡️ Mitigation Workflow', icon: Shield },
    { id: 'api-reference', title: '📋 API Reference', icon: Code }
  ];

  const renderCodeBlock = (code, language, identifier) => (
    <div className="code-block">
      <div className="code-header">
        <span className="code-language">{language}</span>
        <button
          className={`copy-btn ${copiedCode === identifier ? 'copied' : ''}`}
          onClick={() => copyToClipboard(code, identifier)}
        >
          {copiedCode === identifier ? <CheckCircle size={16} /> : <Copy size={16} />}
          {copiedCode === identifier ? 'Copied!' : 'Copy'}
        </button>
      </div>
      <pre><code>{code}</code></pre>
    </div>
  );

  return (
    <div className="documentation">
      <div className="documentation-header">
        <BookOpen size={32} className="documentation-icon" />
        <div>
          <h1>Agentic Threat Modeler Documentation</h1>
          <p>Comprehensive guide for integrating AI-powered security scanning into your development workflow</p>
        </div>
      </div>

      <div className="documentation-content">
        <div className="documentation-sidebar">
          <nav className="documentation-nav">
            {sections.map((section) => {
              const IconComponent = section.icon;
              return (
                <button
                  key={section.id}
                  className={`nav-item ${activeSection === section.id ? 'active' : ''}`}
                  onClick={() => setActiveSection(section.id)}
                >
                  <IconComponent size={18} />
                  <span>{section.title}</span>
                </button>
              );
            })}
          </nav>
        </div>

        <div className="documentation-main">
          {/* Overview Section */}
          {activeSection === 'overview' && (
            <div className="doc-section">
              <h2>🎯 What is Agentic Threat Modeler?</h2>

              <div className="overview-cards">
                <div className="overview-card">
                  <Bot size={32} />
                  <h3>AI-Powered Analysis</h3>
                  <p>Uses advanced language models to automatically identify security threats and compliance gaps in your systems using industry-standard frameworks.</p>
                </div>

                <div className="overview-card">
                  <Shield size={32} />
                  <h3>Multi-Framework Security</h3>
                  <p>Applies OWASP, STRIDE, MITRE ATT&CK, MAESTRO, and LINDDUN frameworks to provide comprehensive threat coverage.</p>
                </div>

                <div className="overview-card">
                  <Search size={32} />
                  <h3>MCP & Compliance Scanning</h3>
                  <p>Automatically detects Model Context Protocol implementations and validates ISO 42001 AI governance compliance.</p>
                </div>

                <div className="overview-card">
                  <GitBranch size={32} />
                  <h3>CI/CD Integration</h3>
                  <p>Seamlessly integrates into your development pipeline with GitHub Actions, Azure DevOps, GitLab CI, and AWS CodePipeline.</p>
                </div>
              </div>

              <div className="security-benefits">
                <h3>🔐 How This Makes Your System More Secure</h3>

                <div className="benefit-list">
                  <div className="benefit-item">
                    <CheckCircle size={20} className="benefit-icon" />
                    <div>
                      <strong>Proactive Threat Detection:</strong> Identifies vulnerabilities before they can be exploited by analyzing your system architecture against known attack patterns.
                    </div>
                  </div>

                  <div className="benefit-item">
                    <CheckCircle size={20} className="benefit-icon" />
                    <div>
                      <strong>Continuous Monitoring:</strong> Automatically scans your repositories and systems at configured intervals to catch new threats as your codebase evolves.
                    </div>
                  </div>

                  <div className="benefit-item">
                    <CheckCircle size={20} className="benefit-icon" />
                    <div>
                      <strong>Compliance Assurance:</strong> Validates adherence to ISO 42001 AI governance standards, ensuring responsible AI development practices.
                    </div>
                  </div>

                  <div className="benefit-item">
                    <CheckCircle size={20} className="benefit-icon" />
                    <div>
                      <strong>Shift-Left Security:</strong> Integrates security analysis early in the development cycle, making fixes cheaper and faster to implement.
                    </div>
                  </div>

                  <div className="benefit-item">
                    <CheckCircle size={20} className="benefit-icon" />
                    <div>
                      <strong>Actionable Mitigation:</strong> Provides specific, implementable security recommendations with automatic task creation for development teams.
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* GitHub Setup Section */}
          {activeSection === 'github-setup' && (
            <div className="doc-section">
              <h2>🔧 GitHub Organization Scanning Setup</h2>

              <div className="setup-steps">
                <div className="setup-step">
                  <div className="step-number">1</div>
                  <div className="step-content">
                    <h3>Create GitHub Personal Access Token</h3>
                    <p>Generate a token with appropriate permissions for repository scanning:</p>

                    <div className="step-instructions">
                      <ol>
                        <li>Go to GitHub Settings → Developer settings → Personal access tokens → Tokens (classic)</li>
                        <li>Click "Generate new token" → "Generate new token (classic)"</li>
                        <li>Set expiration (recommend 90 days for security)</li>
                        <li>Select the following scopes:</li>
                      </ol>

                      <div className="permission-list">
                        <div className="permission-item">
                          <Code size={16} />
                          <strong>repo</strong> - Full control of private repositories
                        </div>
                        <div className="permission-item">
                          <FileText size={16} />
                          <strong>read:org</strong> - Read org and team membership
                        </div>
                        <div className="permission-item">
                          <Database size={16} />
                          <strong>read:repo_hook</strong> - Read repository hooks
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="setup-step">
                  <div className="step-number">2</div>
                  <div className="step-content">
                    <h3>Add Organization to Scanner</h3>
                    <p>Configure your GitHub organization for automated scanning:</p>

                    <div className="step-instructions">
                      <ol>
                        <li>Navigate to the "GitHub Organization Scanner" tab</li>
                        <li>Click "Add New Organization"</li>
                        <li>Enter your organization name and GitHub token</li>
                        <li>Configure scanning frequency (hourly, daily, weekly, or custom cron)</li>
                        <li>Set notification thresholds and preferences</li>
                        <li>Click "Save Configuration"</li>
                      </ol>
                    </div>
                  </div>
                </div>

                <div className="setup-step">
                  <div className="step-number">3</div>
                  <div className="step-content">
                    <h3>Initiate First Scan</h3>
                    <p>Start your first security scan to baseline your organization:</p>

                    <div className="step-instructions">
                      <ol>
                        <li>From the Organizations list, find your configured organization</li>
                        <li>Click "Scan Now" to initiate an immediate scan</li>
                        <li>Monitor progress in the Dashboard → Scan History tab</li>
                        <li>Review results in the Detections and Compliance tabs</li>
                        <li>Check Notifications for any immediate security alerts</li>
                      </ol>
                    </div>
                  </div>
                </div>
              </div>

              <div className="warning-box">
                <AlertTriangle size={20} />
                <div>
                  <strong>Security Note:</strong> Store your GitHub tokens securely. Never commit tokens to version control.
                  Use environment variables or secure secret management systems in production deployments.
                </div>
              </div>
            </div>
          )}

          {/* CI/CD Integration Section */}
          {activeSection === 'ci-cd' && (
            <div className="doc-section">
              <h2>🔄 CI/CD Pipeline Integration</h2>

              <div className="cicd-overview">
                <p>Integrate automated threat modeling into your CI/CD pipeline to catch security issues early. Reports from pipeline scans appear in the <strong>CI/CD Reports</strong> tab.</p>
              </div>

              {/* Step 1: Create API Token */}
              <div className="setup-section">
                <h3>🔑 Step 1: Create an API Token</h3>
                <p>Generate an API token from your profile settings to authenticate pipeline requests:</p>

                <div className="step-instructions">
                  <ol>
                    <li>Navigate to your <strong>User Profile</strong> (click your avatar in the top right)</li>
                    <li>Go to the <strong>CI/CD Integration</strong> tab</li>
                    <li>Click <strong>Create New Token</strong></li>
                    <li>Enter a name (e.g., "GitHub Actions - MyRepo")</li>
                    <li>Select token type: <code>ci_cd</code></li>
                    <li>Set expiration (90 days recommended for security)</li>
                    <li>Click <strong>Create Token</strong></li>
                    <li><strong>Important:</strong> Copy the token immediately - it won't be shown again!</li>
                  </ol>
                </div>

                <div className="warning-box">
                  <AlertTriangle size={20} />
                  <div>
                    <strong>Security Note:</strong> Store your API token as a secret in your CI/CD platform. Never commit tokens to version control or expose them in logs.
                  </div>
                </div>
              </div>

              {/* Step 2: CI/CD API Endpoints */}
              <div className="setup-section">
                <h3>📡 Step 2: CI/CD API Endpoints</h3>
                <p>Use these dedicated endpoints for pipeline integration:</p>

                <div className="endpoint-list">
                  <div className="endpoint">
                    <div className="endpoint-header">
                      <span className="method post">POST</span>
                      <span className="path">/api/cicd/analyze</span>
                    </div>
                    <p>Submit a system for threat modeling analysis</p>
                  </div>

                  <div className="endpoint">
                    <div className="endpoint-header">
                      <span className="method get">GET</span>
                      <span className="path">/api/cicd/status/&#123;job_id&#125;</span>
                    </div>
                    <p>Poll job status (for CI/CD polling)</p>
                  </div>

                  <div className="endpoint">
                    <div className="endpoint-header">
                      <span className="method get">GET</span>
                      <span className="path">/api/cicd/results/&#123;job_id&#125;?format=json|sarif|markdown</span>
                    </div>
                    <p>Get results in JSON, SARIF (for GitHub Security), or Markdown format</p>
                  </div>
                </div>

                <h4>Request Body for /api/cicd/analyze:</h4>
                {renderCodeBlock(`{
  "system_description": "Your system architecture description...",
  "system_type": "web_app",        // general, web_app, api, agentic_ai, mcp_system
  "role": "baseline",              // baseline, cloud_security, network_security, ai_security
  "enable_risk_scoring": true,
  "selected_frameworks": ["owasp", "stride", "mitre"],
  "repository_url": "https://github.com/owner/repo",
  "branch": "main",
  "commit_sha": "abc123...",
  "pipeline_name": "GitHub Actions"
}`, 'json', 'cicd-request')}

                <h4>Response:</h4>
                {renderCodeBlock(`{
  "job_id": "550e8400-e29b-41d4-a716-446655440000",
  "status": "pending",
  "message": "Threat modeling analysis started from CI/CD pipeline",
  "poll_url": "https://agenticthreat.ai/api/cicd/status/550e8400-...",
  "results_url": "https://agenticthreat.ai/api/cicd/results/550e8400-..."
}`, 'json', 'cicd-response')}
              </div>

              {/* Step 3: Pipeline Examples */}
              <div className="integration-tabs">
                <h3>🚀 Step 3: Pipeline Configuration Examples</h3>

                <div className="ci-cd-provider">
                  <h3>🐙 GitHub Actions (Complete Example)</h3>
                  <p>Full workflow with PR comments and SARIF upload:</p>

                  {renderCodeBlock(`# .github/workflows/threat-modeling.yml
name: Security Threat Analysis

on:
  push:
    branches: [ main, develop ]
  pull_request:
    branches: [ main ]

jobs:
  threat-analysis:
    runs-on: ubuntu-latest
    permissions:
      contents: read
      pull-requests: write
      security-events: write  # For SARIF upload

    steps:
    - name: Checkout code
      uses: actions/checkout@v4

    - name: Read system description
      id: read-desc
      run: |
        # Read from a file or generate dynamically
        if [ -f "SYSTEM_DESCRIPTION.md" ]; then
          DESC=$(cat SYSTEM_DESCRIPTION.md | jq -Rs .)
        else
          DESC='"Web application with React frontend, Node.js API, and PostgreSQL database"'
        fi
        echo "description=$DESC" >> $GITHUB_OUTPUT

    - name: Submit Threat Model Analysis
      id: submit
      run: |
        RESPONSE=$(curl -s -X POST "https://agenticthreat.ai/api/cicd/analyze" \\
          -H "X-API-Token: \${{ secrets.THREATMODELER_TOKEN }}" \\
          -H "Content-Type: application/json" \\
          -d '{
            "system_description": \${{ steps.read-desc.outputs.description }},
            "system_type": "web_app",
            "role": "baseline",
            "enable_risk_scoring": true,
            "repository_url": "\${{ github.server_url }}/\${{ github.repository }}",
            "branch": "\${{ github.ref_name }}",
            "commit_sha": "\${{ github.sha }}",
            "pipeline_name": "GitHub Actions"
          }')

        JOB_ID=$(echo "$RESPONSE" | jq -r '.job_id')
        echo "job_id=$JOB_ID" >> $GITHUB_OUTPUT
        echo "Submitted analysis job: $JOB_ID"

    - name: Wait for Analysis to Complete
      id: wait
      run: |
        JOB_ID="\${{ steps.submit.outputs.job_id }}"
        echo "Waiting for job $JOB_ID to complete..."

        for i in {1..60}; do
          STATUS=$(curl -s "https://agenticthreat.ai/api/cicd/status/$JOB_ID" \\
            -H "X-API-Token: \${{ secrets.THREATMODELER_TOKEN }}" | jq -r '.status')

          echo "Status: $STATUS (attempt $i/60)"

          if [ "$STATUS" = "completed" ]; then
            echo "status=completed" >> $GITHUB_OUTPUT
            exit 0
          elif [ "$STATUS" = "failed" ]; then
            echo "status=failed" >> $GITHUB_OUTPUT
            exit 1
          fi

          sleep 10
        done

        echo "Timeout waiting for analysis"
        exit 1

    - name: Get Results and Post PR Comment
      if: github.event_name == 'pull_request' && steps.wait.outputs.status == 'completed'
      run: |
        JOB_ID="\${{ steps.submit.outputs.job_id }}"

        # Get markdown results
        RESULTS=$(curl -s "https://agenticthreat.ai/api/cicd/results/$JOB_ID?format=markdown" \\
          -H "X-API-Token: \${{ secrets.THREATMODELER_TOKEN }}")

        # Get JSON for threat count
        JSON_RESULTS=$(curl -s "https://agenticthreat.ai/api/cicd/results/$JOB_ID?format=json" \\
          -H "X-API-Token: \${{ secrets.THREATMODELER_TOKEN }}")
        THREAT_COUNT=$(echo "$JSON_RESULTS" | jq -r '.threat_count')

        # Post comment to PR
        COMMENT="## 🔒 Threat Model Analysis Results\\n\\n"
        COMMENT+="**Threats Identified:** $THREAT_COUNT\\n\\n"
        COMMENT+="<details>\\n<summary>View Full Report</summary>\\n\\n"
        COMMENT+="$RESULTS\\n\\n"
        COMMENT+="</details>\\n\\n"
        COMMENT+="[View in Dashboard](https://agenticthreat.ai)"

        gh pr comment \${{ github.event.number }} --body "$COMMENT"
      env:
        GH_TOKEN: \${{ secrets.GITHUB_TOKEN }}

    - name: Upload SARIF to GitHub Security
      if: steps.wait.outputs.status == 'completed'
      run: |
        JOB_ID="\${{ steps.submit.outputs.job_id }}"

        # Download SARIF file
        curl -s "https://agenticthreat.ai/api/cicd/results/$JOB_ID?format=sarif" \\
          -H "X-API-Token: \${{ secrets.THREATMODELER_TOKEN }}" \\
          -o threat-model.sarif

        # Upload to GitHub Security tab
        gh api /repos/\${{ github.repository }}/code-scanning/sarifs \\
          -X POST \\
          -H "Accept: application/vnd.github+json" \\
          -f "commit_sha=\${{ github.sha }}" \\
          -f "ref=\${{ github.ref }}" \\
          -f "sarif=@threat-model.sarif"
      env:
        GH_TOKEN: \${{ secrets.GITHUB_TOKEN }}

    - name: Fail if High-Severity Threats Found
      if: steps.wait.outputs.status == 'completed'
      run: |
        JOB_ID="\${{ steps.submit.outputs.job_id }}"

        # Check for high-severity threats
        RESULTS=$(curl -s "https://agenticthreat.ai/api/cicd/results/$JOB_ID?format=json" \\
          -H "X-API-Token: \${{ secrets.THREATMODELER_TOKEN }}")

        HIGH_COUNT=$(echo "$RESULTS" | jq '[.results.threat_model[] | select(.cvss_score >= 7.0)] | length')

        if [ "$HIGH_COUNT" -gt 0 ]; then
          echo "::error::Found $HIGH_COUNT high-severity threats (CVSS >= 7.0)"
          echo "Review the Security tab or PR comment for details"
          exit 1
        fi

        echo "No high-severity threats found"`, 'yaml', 'github-actions-complete')}

                  <div className="setup-note">
                    <Info size={16} />
                    <span><strong>Required Secret:</strong> Add <code>THREATMODELER_TOKEN</code> to your repository secrets (Settings → Secrets → Actions).</span>
                  </div>
                </div>

                <div className="ci-cd-provider">
                  <h3>🔵 Azure DevOps Pipeline</h3>
                  <p>Full pipeline with PR comments and build artifacts:</p>

                  {renderCodeBlock(`# azure-pipelines.yml
trigger:
  branches:
    include:
    - main
    - develop

pr:
  branches:
    include:
    - main

variables:
  - group: ThreatModeler-Secrets  # Contains THREATMODELER_TOKEN

stages:
- stage: SecurityAnalysis
  displayName: 'Security Threat Analysis'
  jobs:
  - job: ThreatModeling
    displayName: 'Run Threat Model Analysis'
    pool:
      vmImage: 'ubuntu-latest'

    steps:
    - checkout: self

    - task: Bash@3
      name: SubmitAnalysis
      displayName: 'Submit Threat Model Analysis'
      inputs:
        targetType: 'inline'
        script: |
          RESPONSE=$(curl -s -X POST "https://agenticthreat.ai/api/cicd/analyze" \\
            -H "X-API-Token: $(THREATMODELER_TOKEN)" \\
            -H "Content-Type: application/json" \\
            -d '{
              "system_description": "Azure web app with App Service, Azure SQL, and Blob Storage",
              "system_type": "web_app",
              "role": "cloud_security",
              "enable_risk_scoring": true,
              "repository_url": "$(Build.Repository.Uri)",
              "branch": "$(Build.SourceBranchName)",
              "commit_sha": "$(Build.SourceVersion)",
              "pipeline_name": "Azure DevOps"
            }')

          JOB_ID=$(echo "$RESPONSE" | jq -r '.job_id')
          echo "##vso[task.setvariable variable=JobId;isOutput=true]$JOB_ID"
          echo "Submitted job: $JOB_ID"

    - task: Bash@3
      name: WaitForResults
      displayName: 'Wait for Analysis Results'
      inputs:
        targetType: 'inline'
        script: |
          JOB_ID="$(SubmitAnalysis.JobId)"

          for i in {1..60}; do
            STATUS=$(curl -s "https://agenticthreat.ai/api/cicd/status/$JOB_ID" \\
              -H "X-API-Token: $(THREATMODELER_TOKEN)" | jq -r '.status')

            echo "Attempt $i: Status = $STATUS"

            if [ "$STATUS" = "completed" ]; then
              echo "##vso[task.setvariable variable=AnalysisStatus]completed"
              exit 0
            elif [ "$STATUS" = "failed" ]; then
              echo "##vso[task.logissue type=error]Analysis failed"
              exit 1
            fi

            sleep 10
          done

          echo "##vso[task.logissue type=error]Timeout waiting for analysis"
          exit 1

    - task: Bash@3
      displayName: 'Download and Publish Results'
      condition: eq(variables['AnalysisStatus'], 'completed')
      inputs:
        targetType: 'inline'
        script: |
          JOB_ID="$(SubmitAnalysis.JobId)"

          # Download reports
          curl -s "https://agenticthreat.ai/api/cicd/results/$JOB_ID?format=json" \\
            -H "X-API-Token: $(THREATMODELER_TOKEN)" > $(Build.ArtifactStagingDirectory)/threat-model.json

          curl -s "https://agenticthreat.ai/api/cicd/results/$JOB_ID?format=markdown" \\
            -H "X-API-Token: $(THREATMODELER_TOKEN)" > $(Build.ArtifactStagingDirectory)/threat-model.md

    - task: PublishBuildArtifacts@1
      displayName: 'Publish Threat Model Reports'
      condition: eq(variables['AnalysisStatus'], 'completed')
      inputs:
        PathtoPublish: '$(Build.ArtifactStagingDirectory)'
        ArtifactName: 'ThreatModelReports'

    - task: Bash@3
      displayName: 'Post PR Comment'
      condition: and(eq(variables['Build.Reason'], 'PullRequest'), eq(variables['AnalysisStatus'], 'completed'))
      inputs:
        targetType: 'inline'
        script: |
          # Use Azure DevOps REST API to post PR comment
          PR_ID=$(System.PullRequest.PullRequestId)

          THREAT_COUNT=$(jq '.threat_count' $(Build.ArtifactStagingDirectory)/threat-model.json)

          COMMENT="## 🔒 Threat Model Analysis\\n\\n**Threats Found:** $THREAT_COUNT\\n\\nView full report in build artifacts."

          curl -X POST "$(System.CollectionUri)$(System.TeamProject)/_apis/git/repositories/$(Build.Repository.ID)/pullRequests/$PR_ID/threads?api-version=7.0" \\
            -H "Authorization: Bearer $(System.AccessToken)" \\
            -H "Content-Type: application/json" \\
            -d "{\\\"comments\\\": [{\\\"content\\\": \\\"$COMMENT\\\"}]}"
      env:
        SYSTEM_ACCESSTOKEN: $(System.AccessToken)`, 'yaml', 'azure-pipeline-complete')}
                </div>

                <div className="ci-cd-provider">
                  <h3>🦊 GitLab CI</h3>
                  <p>Full pipeline with merge request comments:</p>

                  {renderCodeBlock(`# .gitlab-ci.yml
stages:
  - security

variables:
  THREATMODELER_URL: "https://agenticthreat.ai"

threat-modeling:
  stage: security
  image: alpine:latest
  before_script:
    - apk add --no-cache curl jq
  script:
    - |
      echo "Submitting threat model analysis..."

      RESPONSE=$(curl -s -X POST "$THREATMODELER_URL/api/cicd/analyze" \\
        -H "X-API-Token: $THREATMODELER_TOKEN" \\
        -H "Content-Type: application/json" \\
        -d "{
          \\"system_description\\": \\"GitLab application with Ruby on Rails backend\\",
          \\"system_type\\": \\"web_app\\",
          \\"role\\": \\"baseline\\",
          \\"enable_risk_scoring\\": true,
          \\"repository_url\\": \\"$CI_PROJECT_URL\\",
          \\"branch\\": \\"$CI_COMMIT_REF_NAME\\",
          \\"commit_sha\\": \\"$CI_COMMIT_SHA\\",
          \\"pipeline_name\\": \\"GitLab CI\\"
        }")

      JOB_ID=$(echo "$RESPONSE" | jq -r '.job_id')
      echo "Job ID: $JOB_ID"

      # Wait for completion
      for i in $(seq 1 60); do
        STATUS=$(curl -s "$THREATMODELER_URL/api/cicd/status/$JOB_ID" \\
          -H "X-API-Token: $THREATMODELER_TOKEN" | jq -r '.status')

        echo "Attempt $i: Status = $STATUS"

        if [ "$STATUS" = "completed" ]; then
          break
        elif [ "$STATUS" = "failed" ]; then
          echo "Analysis failed!"
          exit 1
        fi

        sleep 10
      done

      # Get results
      curl -s "$THREATMODELER_URL/api/cicd/results/$JOB_ID?format=json" \\
        -H "X-API-Token: $THREATMODELER_TOKEN" > threat-model.json

      THREAT_COUNT=$(jq '.threat_count' threat-model.json)
      echo "Found $THREAT_COUNT threats"

      # Post MR comment if this is a merge request
      if [ -n "$CI_MERGE_REQUEST_IID" ]; then
        COMMENT="## 🔒 Threat Model Analysis\\n\\n**Threats Found:** $THREAT_COUNT\\n\\nView the CI/CD Reports tab for details."

        curl -s -X POST "$CI_API_V4_URL/projects/$CI_PROJECT_ID/merge_requests/$CI_MERGE_REQUEST_IID/notes" \\
          -H "PRIVATE-TOKEN: $GITLAB_TOKEN" \\
          -d "body=$COMMENT"
      fi

      # Fail if high-severity threats
      HIGH_COUNT=$(jq '[.results.threat_model[] | select(.cvss_score >= 7.0)] | length' threat-model.json)
      if [ "$HIGH_COUNT" -gt 0 ]; then
        echo "Found $HIGH_COUNT high-severity threats!"
        exit 1
      fi
  artifacts:
    paths:
      - threat-model.json
    expire_in: 30 days
  rules:
    - if: $CI_COMMIT_BRANCH == "main"
    - if: $CI_COMMIT_BRANCH == "develop"
    - if: $CI_MERGE_REQUEST_IID`, 'yaml', 'gitlab-ci-complete')}

                  <div className="setup-note">
                    <Info size={16} />
                    <span><strong>Required Variables:</strong> Add <code>THREATMODELER_TOKEN</code> and <code>GITLAB_TOKEN</code> (for MR comments) to CI/CD Settings → Variables.</span>
                  </div>
                </div>

                <div className="ci-cd-provider">
                  <h3>🟢 Jenkins Pipeline</h3>
                  <p>Declarative pipeline with results archiving:</p>

                  {renderCodeBlock(`// Jenkinsfile
pipeline {
    agent any

    environment {
        THREATMODELER_TOKEN = credentials('threatmodeler-token')
        THREATMODELER_URL = 'https://agenticthreat.ai'
    }

    stages {
        stage('Threat Model Analysis') {
            steps {
                script {
                    // Submit analysis
                    def response = sh(
                        script: """
                            curl -s -X POST "\${THREATMODELER_URL}/api/cicd/analyze" \\
                              -H "X-API-Token: \${THREATMODELER_TOKEN}" \\
                              -H "Content-Type: application/json" \\
                              -d '{
                                "system_description": "Jenkins-deployed application",
                                "system_type": "web_app",
                                "repository_url": "\${GIT_URL}",
                                "branch": "\${GIT_BRANCH}",
                                "commit_sha": "\${GIT_COMMIT}",
                                "pipeline_name": "Jenkins"
                              }'
                        """,
                        returnStdout: true
                    ).trim()

                    def json = readJSON text: response
                    env.JOB_ID = json.job_id
                    echo "Submitted job: \${env.JOB_ID}"
                }
            }
        }

        stage('Wait for Results') {
            steps {
                script {
                    def completed = false
                    for (int i = 0; i < 60; i++) {
                        def status = sh(
                            script: """
                                curl -s "\${THREATMODELER_URL}/api/cicd/status/\${JOB_ID}" \\
                                  -H "X-API-Token: \${THREATMODELER_TOKEN}" | jq -r '.status'
                            """,
                            returnStdout: true
                        ).trim()

                        echo "Status: \${status}"

                        if (status == 'completed') {
                            completed = true
                            break
                        } else if (status == 'failed') {
                            error "Threat analysis failed"
                        }

                        sleep 10
                    }

                    if (!completed) {
                        error "Timeout waiting for analysis"
                    }
                }
            }
        }

        stage('Process Results') {
            steps {
                sh """
                    curl -s "\${THREATMODELER_URL}/api/cicd/results/\${JOB_ID}?format=json" \\
                      -H "X-API-Token: \${THREATMODELER_TOKEN}" > threat-model.json

                    curl -s "\${THREATMODELER_URL}/api/cicd/results/\${JOB_ID}?format=markdown" \\
                      -H "X-API-Token: \${THREATMODELER_TOKEN}" > threat-model.md
                """

                archiveArtifacts artifacts: 'threat-model.*', fingerprint: true

                script {
                    def results = readJSON file: 'threat-model.json'
                    def threatCount = results.threat_count

                    echo "Found \${threatCount} threats"

                    // Fail build if high-severity threats
                    def highSeverity = results.results.threat_model.findAll { it.cvss_score >= 7.0 }
                    if (highSeverity.size() > 0) {
                        unstable "Found \${highSeverity.size()} high-severity threats"
                    }
                }
            }
        }
    }

    post {
        always {
            publishHTML([
                reportName: 'Threat Model Report',
                reportDir: '.',
                reportFiles: 'threat-model.md',
                keepAll: true
            ])
        }
    }
}`, 'groovy', 'jenkins-pipeline')}
                </div>
              </div>

              {/* Result Posting Options */}
              <div className="setup-section">
                <h3>📤 Posting Results Back to Your Pipeline</h3>
                <p>Choose how you want to surface threat model results in your workflow:</p>

                <div className="result-options">
                  <div className="result-option">
                    <h4>🔒 GitHub Security Tab (SARIF)</h4>
                    <p>Upload results to GitHub's Security tab for native vulnerability tracking:</p>
                    {renderCodeBlock(`# Download SARIF and upload to GitHub Security
curl -s "https://agenticthreat.ai/api/cicd/results/$JOB_ID?format=sarif" \\
  -H "X-API-Token: $TOKEN" -o results.sarif

gh api /repos/OWNER/REPO/code-scanning/sarifs \\
  -X POST -f "commit_sha=$SHA" -f "ref=$REF" -f "sarif=@results.sarif"`, 'bash', 'sarif-upload')}
                  </div>

                  <div className="result-option">
                    <h4>💬 PR/MR Comments</h4>
                    <p>Post a summary comment on pull/merge requests:</p>
                    {renderCodeBlock(`# Get markdown summary and post to PR
RESULTS=$(curl -s "https://agenticthreat.ai/api/cicd/results/$JOB_ID?format=markdown" \\
  -H "X-API-Token: $TOKEN")

# GitHub
gh pr comment $PR_NUMBER --body "$RESULTS"

# GitLab
curl -X POST "$CI_API_V4_URL/projects/$PROJECT_ID/merge_requests/$MR_IID/notes" \\
  -H "PRIVATE-TOKEN: $GITLAB_TOKEN" -d "body=$RESULTS"`, 'bash', 'pr-comment')}
                  </div>

                  <div className="result-option">
                    <h4>📊 Build Artifacts</h4>
                    <p>Save reports as build artifacts for later review:</p>
                    {renderCodeBlock(`# Download all formats
curl -s "https://agenticthreat.ai/api/cicd/results/$JOB_ID?format=json" \\
  -H "X-API-Token: $TOKEN" > threat-model.json

curl -s "https://agenticthreat.ai/api/cicd/results/$JOB_ID?format=markdown" \\
  -H "X-API-Token: $TOKEN" > threat-model.md

curl -s "https://agenticthreat.ai/api/cicd/results/$JOB_ID?format=sarif" \\
  -H "X-API-Token: $TOKEN" > threat-model.sarif`, 'bash', 'artifacts')}
                  </div>

                  <div className="result-option">
                    <h4>🚫 Fail on High-Severity Threats</h4>
                    <p>Break the build if critical issues are found:</p>
                    {renderCodeBlock(`# Check for high-severity threats (CVSS >= 7.0)
RESULTS=$(curl -s "https://agenticthreat.ai/api/cicd/results/$JOB_ID?format=json" \\
  -H "X-API-Token: $TOKEN")

HIGH_COUNT=$(echo "$RESULTS" | jq '[.results.threat_model[] | select(.cvss_score >= 7.0)] | length')

if [ "$HIGH_COUNT" -gt 0 ]; then
  echo "FAILED: Found $HIGH_COUNT high-severity threats"
  exit 1
fi

echo "PASSED: No high-severity threats found"`, 'bash', 'fail-check')}
                  </div>
                </div>
              </div>

              <div className="integration-note">
                <h4>📋 Quick Reference</h4>
                <table className="reference-table">
                  <thead>
                    <tr>
                      <th>Platform</th>
                      <th>Secret Name</th>
                      <th>PR Comment Method</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>GitHub Actions</td>
                      <td><code>THREATMODELER_TOKEN</code></td>
                      <td><code>gh pr comment</code></td>
                    </tr>
                    <tr>
                      <td>Azure DevOps</td>
                      <td>Variable Group</td>
                      <td>REST API with <code>System.AccessToken</code></td>
                    </tr>
                    <tr>
                      <td>GitLab CI</td>
                      <td>CI/CD Variable</td>
                      <td>Notes API with <code>PRIVATE-TOKEN</code></td>
                    </tr>
                    <tr>
                      <td>Jenkins</td>
                      <td>Credentials</td>
                      <td>GitHub/GitLab plugin or REST API</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Scan Types Section */}
          {activeSection === 'scan-types' && (
            <div className="doc-section">
              <h2>🔍 Comprehensive Scan Types</h2>

              <div className="scan-types">
                <div className="scan-type">
                  <div className="scan-header">
                    <Bot size={24} />
                    <h3>MCP (Model Context Protocol) Detection</h3>
                  </div>

                  <p>Identifies implementations of Model Context Protocol in your codebase to ensure secure AI integrations.</p>

                  <div className="scan-details">
                    <h4>What it Scans:</h4>
                    <div className="detection-categories">
                      <div className="category">
                        <FileText size={16} />
                        <span><strong>Schema Files:</strong> MCP protocol schema definitions and configuration files</span>
                      </div>
                      <div className="category">
                        <Settings size={16} />
                        <span><strong>Configuration:</strong> MCP server/client configuration and transport settings</span>
                      </div>
                      <div className="category">
                        <Code size={16} />
                        <span><strong>Client Code:</strong> MCP client implementations and API integrations</span>
                      </div>
                      <div className="category">
                        <Database size={16} />
                        <span><strong>Server Code:</strong> MCP server implementations and resource handlers</span>
                      </div>
                      <div className="category">
                        <Bot size={16} />
                        <span><strong>Tool Definitions:</strong> MCP tool and prompt definitions</span>
                      </div>
                      <div className="category">
                        <Globe size={16} />
                        <span><strong>Protocol Implementation:</strong> Core MCP protocol implementation code</span>
                      </div>
                      <div className="category">
                        <FileText size={16} />
                        <span><strong>Package Dependencies:</strong> MCP-related packages and dependencies</span>
                      </div>
                    </div>

                    <h4>Security Benefits:</h4>
                    <ul>
                      <li>Prevents insecure AI model integrations</li>
                      <li>Identifies potential data leakage points in AI workflows</li>
                      <li>Ensures proper authentication and authorization in MCP implementations</li>
                      <li>Validates secure communication channels between AI components</li>
                    </ul>
                  </div>
                </div>

                <div className="scan-type">
                  <div className="scan-header">
                    <CheckCircle size={24} />
                    <h3>ISO 42001 AI Compliance Scanning</h3>
                  </div>

                  <p>Validates adherence to ISO/IEC 42001:2023 Artificial Intelligence Management System standards.</p>

                  <div className="scan-details">
                    <h4>Compliance Areas Checked:</h4>
                    <div className="compliance-categories">
                      <div className="category">
                        <FileText size={16} />
                        <span><strong>AI Policy Documentation:</strong> AI governance policies and procedures</span>
                      </div>
                      <div className="category">
                        <AlertTriangle size={16} />
                        <span><strong>Risk Management:</strong> AI risk assessment and mitigation frameworks</span>
                      </div>
                      <div className="category">
                        <Database size={16} />
                        <span><strong>Data Governance:</strong> Data quality, lineage, and lifecycle management</span>
                      </div>
                      <div className="category">
                        <Bot size={16} />
                        <span><strong>Model Lifecycle:</strong> AI model development and deployment processes</span>
                      </div>
                      <div className="category">
                        <CheckCircle size={16} />
                        <span><strong>Testing & Validation:</strong> AI system testing and validation procedures</span>
                      </div>
                      <div className="category">
                        <Search size={16} />
                        <span><strong>Monitoring & Auditing:</strong> Continuous monitoring and audit trails</span>
                      </div>
                      <div className="category">
                        <FileText size={16} />
                        <span><strong>Documentation:</strong> Technical documentation and change management</span>
                      </div>
                      <div className="category">
                        <Shield size={16} />
                        <span><strong>Security Controls:</strong> AI-specific security measures and controls</span>
                      </div>
                      <div className="category">
                        <Globe size={16} />
                        <span><strong>Third-Party Management:</strong> Vendor and third-party AI service management</span>
                      </div>
                    </div>

                    <h4>Security Benefits:</h4>
                    <ul>
                      <li>Ensures responsible AI development practices</li>
                      <li>Reduces regulatory compliance risks</li>
                      <li>Establishes accountability in AI decision-making</li>
                      <li>Improves AI system transparency and explainability</li>
                      <li>Minimizes bias and fairness issues in AI systems</li>
                    </ul>
                  </div>
                </div>

                <div className="scan-type">
                  <div className="scan-header">
                    <Shield size={24} />
                    <h3>Multi-Framework Threat Analysis</h3>
                  </div>

                  <p>Comprehensive security analysis using industry-standard threat modeling frameworks.</p>

                  <div className="scan-details">
                    <h4>Applied Frameworks:</h4>
                    <div className="framework-list">
                      <div className="framework">
                        <h5>🔴 OWASP Top 10</h5>
                        <p>Web application security risks including injection, broken authentication, and security misconfigurations.</p>
                      </div>
                      <div className="framework">
                        <h5>🤖 OWASP LLM Top 10</h5>
                        <p>LLM-specific vulnerabilities like prompt injection, data leakage, and model denial of service.</p>
                      </div>
                      <div className="framework">
                        <h5>🏗️ OWASP Agentic (T1-T15)</h5>
                        <p>Comprehensive agentic AI threat taxonomy covering autonomous agent risks, decision-making vulnerabilities, and multi-agent system threats.</p>
                      </div>
                      <div className="framework">
                        <h5>🔗 OWASP MCP Top 10</h5>
                        <p>Model Context Protocol specific threats including schema validation, transport security, and resource access control vulnerabilities.</p>
                      </div>
                      <div className="framework">
                        <h5>⚡ STRIDE</h5>
                        <p>Microsoft's threat modeling methodology covering Spoofing, Tampering, Repudiation, Information disclosure, Denial of service, and Elevation of privilege.</p>
                      </div>
                      <div className="framework">
                        <h5>🎯 MITRE ATT&CK</h5>
                        <p>Adversarial tactics, techniques, and procedures based on real-world observations of cyber attacks.</p>
                      </div>
                      <div className="framework">
                        <h5>🎼 MAESTRO</h5>
                        <p>7-layer AI security framework covering foundation models, supply chain, and deployment security.</p>
                      </div>
                      <div className="framework">
                        <h5>🔒 LINDDUN</h5>
                        <p>Privacy threat modeling framework addressing Linkability, Identifiability, Non-repudiation, Detectability, Disclosure, Unawareness, and Non-compliance.</p>
                      </div>
                      <div className="framework">
                        <h5>📋 ISO 42001</h5>
                        <p>AI management system compliance framework ensuring responsible AI development, governance, and risk management practices according to international standards.</p>
                      </div>
                    </div>

                    <h4>Security Benefits:</h4>
                    <ul>
                      <li>Provides comprehensive threat coverage across multiple security domains</li>
                      <li>Identifies both technical and business logic vulnerabilities</li>
                      <li>Maps threats to real-world attack scenarios</li>
                      <li>Enables prioritized security remediation efforts</li>
                      <li>Supports compliance with various security standards</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Mitigation Workflow Section */}
          {activeSection === 'mitigation' && (
            <div className="doc-section">
              <h2>🛡️ Automated Mitigation Workflow</h2>

              <div className="workflow-overview">
                <p>After each scan completes, the system automatically creates actionable mitigation tasks to help your team address identified security threats and compliance gaps.</p>
              </div>

              <div className="workflow-steps">
                <div className="workflow-step">
                  <div className="step-icon">
                    <Search size={24} />
                  </div>
                  <div className="step-content">
                    <h3>1. Threat Detection & Analysis</h3>
                    <p>The system identifies security threats and compliance issues during the scan process.</p>
                    <ul>
                      <li>MCP implementation vulnerabilities</li>
                      <li>Framework-specific security gaps</li>
                      <li>ISO 42001 compliance deviations</li>
                      <li>Risk scoring and prioritization</li>
                    </ul>
                  </div>
                </div>

                <div className="workflow-step">
                  <div className="step-icon">
                    <Bot size={24} />
                  </div>
                  <div className="step-content">
                    <h3>2. Automatic Task Generation</h3>
                    <p>For each identified threat, the system automatically generates specific mitigation tasks.</p>

                    <div className="task-examples">
                      <h4>Example Generated Tasks:</h4>
                      <div className="task-example">
                        <div className="task-priority high">HIGH</div>
                        <div className="task-details">
                          <strong>Fix SQL Injection Vulnerability</strong>
                          <p>Implement parameterized queries in user authentication module (UserService.py:45)</p>
                          <span className="task-framework">OWASP A03 - Injection</span>
                        </div>
                      </div>

                      <div className="task-example">
                        <div className="task-priority medium">MEDIUM</div>
                        <div className="task-details">
                          <strong>Add MCP Authentication</strong>
                          <p>Implement proper authentication for MCP server endpoints in config/mcp_server.json</p>
                          <span className="task-framework">MCP Security - Authentication</span>
                        </div>
                      </div>

                      <div className="task-example">
                        <div className="task-priority low">LOW</div>
                        <div className="task-details">
                          <strong>Update AI Risk Documentation</strong>
                          <p>Add AI risk assessment documentation to meet ISO 42001 requirements in docs/ai-governance/</p>
                          <span className="task-framework">ISO 42001 - Risk Management</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="workflow-step">
                  <div className="step-icon">
                    <GitBranch size={24} />
                  </div>
                  <div className="step-content">
                    <h3>3. Integration with Development Tools</h3>
                    <p>Tasks are automatically created in your existing project management and development tools.</p>

                    <div className="integration-options">
                      <div className="integration-option">
                        <h5>🐙 GitHub Issues</h5>
                        <ul>
                          <li>Creates labeled issues with security and compliance tags</li>
                          <li>Links to specific code locations and line numbers</li>
                          <li>Includes detailed remediation steps and code examples</li>
                          <li>Assigns priority labels based on risk scoring</li>
                        </ul>
                      </div>

                      <div className="integration-option">
                        <h5>🔵 Azure DevOps Work Items</h5>
                        <ul>
                          <li>Creates work items with appropriate work item types</li>
                          <li>Sets priority and severity based on threat analysis</li>
                          <li>Links to repository files and specific commits</li>
                          <li>Assigns to relevant team members based on code ownership</li>
                        </ul>
                      </div>

                      <div className="integration-option">
                        <h5>🦊 GitLab Issues</h5>
                        <ul>
                          <li>Creates issues with security and compliance labels</li>
                          <li>Links to merge requests and specific file changes</li>
                          <li>Includes milestone assignment for sprint planning</li>
                          <li>Sets issue weights based on estimated effort</li>
                        </ul>
                      </div>

                      <div className="integration-option">
                        <h5>📋 Jira Tickets</h5>
                        <ul>
                          <li>Creates security and compliance epic/story/task hierarchies</li>
                          <li>Links to repository and specific code locations</li>
                          <li>Assigns components and fix versions</li>
                          <li>Sets priority and story points for planning</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="workflow-step">
                  <div className="step-icon">
                    <CheckCircle size={24} />
                  </div>
                  <div className="step-content">
                    <h3>4. Validation & Closure</h3>
                    <p>The system tracks mitigation progress and validates fixes in subsequent scans.</p>

                    <div className="validation-process">
                      <h4>Validation Process:</h4>
                      <ul>
                        <li><strong>Re-scan Verification:</strong> Subsequent scans check if identified threats have been resolved</li>
                        <li><strong>Automatic Task Updates:</strong> Tasks are automatically updated when threats are mitigated</li>
                        <li><strong>Progress Tracking:</strong> Dashboard shows mitigation progress and completion rates</li>
                        <li><strong>Regression Detection:</strong> Alerts when previously fixed threats reappear</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>

              <div className="notification-setup">
                <h3>📬 Mitigation Notifications</h3>
                <p>Stay informed about new mitigation tasks and progress updates:</p>

                <div className="notification-channels">
                  <div className="notification-channel">
                    <div className="channel-icon">📧</div>
                    <div>
                      <strong>Email Notifications</strong>
                      <p>Receive daily/weekly digests of new tasks and completion summaries</p>
                    </div>
                  </div>

                  <div className="notification-channel">
                    <div className="channel-icon">💬</div>
                    <div>
                      <strong>Slack Integration</strong>
                      <p>Real-time notifications to security and development channels</p>
                    </div>
                  </div>

                  <div className="notification-channel">
                    <div className="channel-icon">💼</div>
                    <div>
                      <strong>Microsoft Teams</strong>
                      <p>Automated posts to team channels with task summaries</p>
                    </div>
                  </div>

                  <div className="notification-channel">
                    <div className="channel-icon">🔗</div>
                    <div>
                      <strong>Webhook Integration</strong>
                      <p>Custom integrations with your existing notification systems</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* API Reference Section */}
          {activeSection === 'api-reference' && (
            <div className="doc-section">
              <h2>📋 API Reference</h2>

              <div className="api-section">
                <h3>Authentication</h3>
                <p>All API calls require a valid authentication token in the Authorization header:</p>

                {renderCodeBlock(`curl -H "Authorization: Bearer YOUR_TOKEN" \\
     -H "Content-Type: application/json" \\
     https://your-threat-modeler.com/api/endpoint`, 'bash', 'auth-example')}
              </div>

              <div className="api-endpoints">
                <div className="endpoint-group">
                  <h3>🔍 Scanning Endpoints</h3>

                  <div className="endpoint">
                    <div className="endpoint-header">
                      <span className="method post">POST</span>
                      <span className="path">/api/analyze</span>
                    </div>
                    <p>Submit a system for threat analysis</p>

                    {renderCodeBlock(`{
  "system_type": "web_app",
  "role": "baseline",
  "input_text": "Your system description...",
  "enable_risk_scoring": true,
  "selected_frameworks": ["owasp", "stride", "mitre"]
}`, 'json', 'analyze-request')}
                  </div>

                  <div className="endpoint">
                    <div className="endpoint-header">
                      <span className="method get">GET</span>
                      <span className="path">/api/jobs/&#123;job_id&#125;/status</span>
                    </div>
                    <p>Check the status of a threat analysis job</p>
                  </div>

                  <div className="endpoint">
                    <div className="endpoint-header">
                      <span className="method get">GET</span>
                      <span className="path">/api/jobs/&#123;job_id&#125;/results</span>
                    </div>
                    <p>Retrieve the results of a completed analysis</p>
                  </div>
                </div>

                <div className="endpoint-group">
                  <h3>🏢 GitHub Organization Endpoints</h3>

                  <div className="endpoint">
                    <div className="endpoint-header">
                      <span className="method post">POST</span>
                      <span className="path">/api/github/organizations</span>
                    </div>
                    <p>Add a new GitHub organization for scanning</p>

                    {renderCodeBlock(`{
  "name": "my-organization",
  "github_token": "ghp_...",
  "scan_frequency": "daily",
  "notification_threshold": 3,
  "auto_threat_model": true
}`, 'json', 'org-add-request')}
                  </div>

                  <div className="endpoint">
                    <div className="endpoint-header">
                      <span className="method post">POST</span>
                      <span className="path">/api/github/organizations/&#123;org_id&#125;/scan</span>
                    </div>
                    <p>Trigger an immediate scan of a GitHub organization</p>
                  </div>

                  <div className="endpoint">
                    <div className="endpoint-header">
                      <span className="method get">GET</span>
                      <span className="path">/api/github/scan-results</span>
                    </div>
                    <p>Retrieve GitHub organization scan results</p>
                  </div>
                </div>

                <div className="endpoint-group">
                  <h3>🔔 Notification Endpoints</h3>

                  <div className="endpoint">
                    <div className="endpoint-header">
                      <span className="method get">GET</span>
                      <span className="path">/api/notifications</span>
                    </div>
                    <p>Retrieve user notifications</p>
                  </div>

                  <div className="endpoint">
                    <div className="endpoint-header">
                      <span className="method post">POST</span>
                      <span className="path">/api/notifications/preferences</span>
                    </div>
                    <p>Update notification preferences</p>

                    {renderCodeBlock(`{
  "channels": ["email", "slack"],
  "email": "user@example.com",
  "slack_webhook": "https://hooks.slack.com/...",
  "immediate_notifications": true,
  "daily_digest": false
}`, 'json', 'notification-prefs')}
                  </div>
                </div>
              </div>

              <div className="rate-limits">
                <h3>⏱️ Rate Limits</h3>
                <div className="limit-info">
                  <div className="limit-item">
                    <strong>Analysis Submissions:</strong> 10 per hour per user
                  </div>
                  <div className="limit-item">
                    <strong>Status Checks:</strong> 100 per hour per user
                  </div>
                  <div className="limit-item">
                    <strong>GitHub Scans:</strong> 5 per day per organization
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Documentation;